import { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { ONE, PREVIOUS_PAGE } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { UserPaymentStatus } from '@/types';
import { track } from '@/utils';

import { GetStartedContext } from '@/components/GetStarted/context';
import {
  OnboardingTrackingEvent,
  useTrackOnboardingEventMutation,
} from '@/store/api/onboardingTrackingApi';
import { useValidateSubscriptionMutation } from '@/store/api/paymentApi';
import { captureException } from '@/utils/errorMonitoring';

const MembershipPaid = () => {
  const { user, refetchUser } = useCurrentUser();
  const { nextStep } = useContext(GetStartedContext);
  const [trackOnboardingEvent] = useTrackOnboardingEventMutation();
  const [validateSubscription] = useValidateSubscriptionMutation();
  const navigate = useNavigate();
  const validatedUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (user.email && user.id) {
      const validationKey = `${user.id}:${user.email}`;
      if (validatedUserRef.current === validationKey) return;

      validatedUserRef.current = validationKey;
      track('ob_selectplan_cont');

      const syncMembershipPaid = async () => {
        const userPayment = await validateSubscription({
          userId: user.id,
        }).unwrap();

        if (userPayment.status !== UserPaymentStatus.active) {
          nextStep(PREVIOUS_PAGE, true);
        } else {
          try {
            await trackOnboardingEvent({
              event: OnboardingTrackingEvent.USER_PAID_MEMBERSHIP,
            }).unwrap();
          } catch (error) {
            captureException(error);
          }

          await refetchUser();
          // If assessment is complete and user is matched, go to /m
          if (user.userAssesment?.complete && user.hasCoaches) {
            navigate('/m', { replace: true });
          } else {
            nextStep(ONE, true);
          }
        }
      };

      syncMembershipPaid().catch((error) => {
        validatedUserRef.current = null;
        captureException(error);
      });
    }
  }, [
    nextStep,
    navigate,
    refetchUser,
    trackOnboardingEvent,
    user.email,
    user.id,
    validateSubscription,
  ]);

  return <div className="flex w-screen flex-1 flex-col bg-sunny-200" />;
};

export default MembershipPaid;
